import React, { useState, useEffect, useContext } from 'react';
import * as Router from 'react-router-dom';
import * as Antd from 'antd';
import { Button, Col, Dropdown, Input, Menu, Row, Space } from 'antd';
import * as AntdIcons from '@ant-design/icons';
import Fuse from 'fuse.js';
import Lodash from 'lodash';
import Moment from 'moment';
import MDX from '@mdx-js/runtime';
import { MDXProvider } from '@mdx-js/react';
import { CSVLink } from "react-csv";
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';

import * as COM from '.';
import { AuthContext } from '../../containers/App';
import {
  datasetgridColumnsStorage,
  datasetgridFiltersStorage,
  datasetgridSortsStorage
} from '../../storage';

const evalTemplate = (stringFn, params) => {
  return Function(...Object.keys(params), "return `" + stringFn + "`")
    (...Object.values(params));
}

const useQuery = () => {
  const { search } = Router.useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const DatasetGrid = (props) => {
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [dataOrig, setDataOrig] = useState([]);
  const [pagination, setPagination] = useState({ pageSize: props.pageSize ? props.pageSize : 20 });
  const [filters, setFilters] = useState(null);
  const [drawerShowed, setDrawerShowed] = useState(false);
  const [sorter, setSorter] = useState({});
  let queryParams = useQuery();
  let auth = useContext(AuthContext);

  const fuseOptions = {
    shouldSort: true,
    includeScore: true,
    threshold: 0.15,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    minMatchCharLength: 2,
    keys: []
  };

  const STORAGE_KEY = `${props.connector}_${props.dataset}_${props.name ? props.name : 'default'}`;

  const loadColumns = async (forced = false) => {
    let columns = await COM.fetchColumns({
      connector: props.connector,
      dataset: props.dataset,
      name: props.name,
      columns: props.columns,
    }, auth, forced);
    setColumns(columns);
    return columns;
  };

  const loadData = async (filters = null, sorter = null) => {
    let data = await COM.fetchData({
      connector: props.connector,
      dataset: props.dataset,
      filters: filters === null ? props.filters : filters
    }, auth, false);

    if (sorter && sorter.field && sorter.order && sorter.order === "descend") {
      setData(Lodash.orderBy(data, [sorter.field], ['desc']));
      setDataOrig(Lodash.orderBy(data, [sorter.field], ['desc']));
    } else if (sorter && sorter.field && sorter.order && sorter.order === "ascend") {
      setData(Lodash.orderBy(data, [sorter.field], ['asc']));
      setDataOrig(Lodash.orderBy(data, [sorter.field], ['asc']));
    } else {
      setData(data);
      setDataOrig(data);
    }
    return data;
  };

  const init = async (forced = false) => {
    setLoading(true);

    if (props.useURLPagination) {
      if (queryParams.get(`${props.name ? props.name + '_' : ''}page`)) {
        setPagination(Object.assign({}, pagination, { current: parseInt(queryParams.get(`${props.name ? props.name + '_' : ''}page`)) }));
      }
    }

    let filtersItem = await datasetgridFiltersStorage.getItem(STORAGE_KEY);
    if (filtersItem && filtersItem.loaded) {
      if (typeof filtersItem.filters !== 'string') {
        datasetgridFiltersStorage.removeItem(STORAGE_KEY);
      } else {
        setFilters(JSON.parse(filtersItem.filters));
      }
    }

    let sorter = await datasetgridSortsStorage.getItem(STORAGE_KEY);
    if (sorter) {
      setSorter(sorter);
    }

    await loadColumns(forced);
    await loadData(filtersItem && filtersItem.loaded ? JSON.parse(filtersItem.filters) : null, sorter);

    setLoading(false);
  }

  useEffect(() => {
    init();
  }, props.invalidateEffect ? props.invalidateEffect : []);

  const onReloadMenuClicked = async (item) => {
    let keysPath = item.keyPath.reverse();

    if (keysPath[0] === 'data') {
      init(false);
    }

    if (keysPath[0] === 'all') {
      datasetgridFiltersStorage.removeItem(STORAGE_KEY);
      init(true);
    }
  }

  const reloadMenu = (
    <Menu
      onClick={onReloadMenuClicked}
    >
      <Menu.Item key="all">
        Reload all
      </Menu.Item>
    </Menu>
  );

  const onSettingsMenuClicked = async (item) => {
    let keysPath = item.keyPath.reverse();

    if (keysPath[0] === 'visible') {
      for (let column of columns) {
        if (keysPath[1] === column.fieldName) {
          column.settings.datagrid.visible = !column.settings.datagrid.visible;
        }
      }
    }

    if (keysPath[0] === 'fulltext') {
      for (let column of columns) {
        if (keysPath[1] === column.fieldName) {
          column.settings.datagrid.fulltext = !column.settings.datagrid.fulltext;
        }
      }
    }

    let storageKey = `${props.connector}_${props.dataset}_${props.name ? props.name : 'default'}`;
    await datasetgridColumnsStorage.setItem(storageKey, columns);

    await loadColumns();
  }

  const settingsMenu = (
    <Menu
      onClick={onSettingsMenuClicked}
    >
      <Menu.SubMenu key="visible" title="Showed columns">
        {Lodash.sortBy(columns, ['settings.datagrid.order']).map((child) => {
          return child.settings.datagrid.visible
            ? <Menu.Item key={child.fieldName}><AntdIcons.CheckOutlined />{child.name}</Menu.Item>
            : <Menu.Item key={child.fieldName}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{child.name}</Menu.Item>
        })}
      </Menu.SubMenu>
      <Menu.SubMenu key="fulltext" title="Fulltext search">
        {Lodash.sortBy(columns, ['settings.datagrid.order']).map((child) => {
          return child.settings.datagrid.fulltext
            ? <Menu.Item key={child.fieldName}><AntdIcons.CheckOutlined />{child.name}</Menu.Item>
            : <Menu.Item key={child.fieldName}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{child.name}</Menu.Item>
        })}
      </Menu.SubMenu>
    </Menu>
  );

  return (
    <div className="layout-content-main" >
      {props.allowSearch
        || props.allowRowsCount
        || props.extra
        || props.allowReload
        || props.allowExport
        || props.allowFilters
        || props.allowSettings
        ? <Row>
          <Col
            span={24}
          >
            <Space
              style={{
                marginBottom: 10,
              }}
            >
              {props.allowSearch
                ? <Input.Search
                  disabled={loading}
                  allowClear={true}
                  placeholder="fulltext search"
                  onSearch={(value) => {
                    setLoading(true);
                    if (value !== "") {
                      for (let column of columns) {
                        if (column.settings.datagrid.fulltext === true) {
                          fuseOptions.keys.push(column.fieldName);
                        }
                      }
                      let fuse = new Fuse(dataOrig, fuseOptions);
                      let result = fuse.search(value);
                      setData(result.map(child => child.item));
                    } else {
                      setData(dataOrig);
                    }
                    setLoading(false);
                  }}
                  style={{
                    width: 400
                  }}
                />
                : null}
              {props.allowRowsCount
                ? <Antd.Tag
                  style={{
                    padding: '4px 7px'
                  }}
                >
                  Rows: {loading ? <AntdIcons.SyncOutlined spin /> : data.length}
                </Antd.Tag>
                : null}
            </Space>
            <Space
              style={{
                marginBottom: 10,
                float: 'right',
              }}
            >
              {props.extra
                ? props.extra.map(child => child)
                : null}
              {props.allowReload === undefined || props.allowReload
                ? <Dropdown.Button
                  icon={<AntdIcons.DownOutlined />}
                  title={'Reload data'}
                  disabled={loading}
                  trigger={'click'}
                  overlay={reloadMenu}
                  onClick={() => {
                    init(false);
                  }}
                >
                  <AntdIcons.ReloadOutlined /> Reload
                </Dropdown.Button>
                : null}
              {props.allowExport === undefined || props.allowExport
                ? <Button
                  title={'Export XLSX'}
                  disabled={loading}
                  onClick={async () => {
                    const workbook = new ExcelJS.Workbook();
                    const worksheet = workbook.addWorksheet("Sheet1");

                    // prepare columns
                    worksheet.columns = Lodash.orderBy(
                      Lodash
                        .merge(
                          Lodash
                            .filter(columns, column =>
                              column.settings.datagrid.visible
                                ? true
                                : false
                            )
                            .map(column => ({
                              header: column.name,
                              key: column.fieldName,
                              width: column.settings.datagrid.width ? column.settings.datagrid.width / 5 : 10,
                              order: column.settings.datagrid.order ? column.settings.datagrid.order : 0,
                            })),
                          props.exportExtraColumns
                            ? props.exportExtraColumns
                            : []),
                      ['order']);

                    // enable Auto-filter if is allowed
                    if (props.exportAllowAutoFilter) {
                      worksheet.autoFilter = {
                        from: {
                          row: 1,
                          column: 1,
                        },
                        to: {
                          row: 1,
                          column: worksheet.columns.length
                        }
                      }
                    }

                    // freeze header row
                    worksheet.views = [{ state: "frozen", ySplit: 1 }]

                    // Add data rows
                    worksheet.addRows(Lodash.map(data, row => {
                      for (let column of columns) {
                        if (column.settings.datagrid.visible && column.type === 'date') {
                          row[column.fieldName] = new Date(row[column.fieldName]);
                        }
                      }
                      return row;
                    }));

                    // Write and download
                    let buffer = await workbook.xlsx.writeBuffer();
                    saveAs(
                      new Blob([buffer], { type: "application/octet-stream" }),
                      `${Moment().format('YYMMDDHHmmss')}_${props.dataset}_${props.name ? props.name : 'default'}.xlsx`
                    );
                  }}
                >
                  <AntdIcons.ExportOutlined /> Export
                </Button>
                : null}
              {props.allowFilters
                ? <Button
                  title={'Filters'}
                  disabled={loading}
                  onClick={() => {
                    setDrawerShowed(true);
                  }}
                >
                  <AntdIcons.FilterFilled /> Filters
                </Button>
                : null}
              {props.allowSettings
                ? <Dropdown
                  trigger={'click'}
                  overlay={settingsMenu}
                  disabled={loading}
                >
                  <Button
                    title={'Settings'}
                  >
                    <AntdIcons.SettingOutlined /> Settings
                  </Button>
                </Dropdown>
                : null}
            </Space>
          </Col>
        </Row>
        : null}
      {props.allowFiltersTags === undefined
        || props.allowFiltersTags === true
        ? <Antd.Row
          style={{
            marginBottom: 10,
          }}
        >
          <Antd.Col span={24}>
            <COM.DatasetFiltersTags
              dataset={props.dataset}
              filters={filters === null ? props.filters : filters}
            />
          </Antd.Col>
        </Antd.Row>
        : null}
      {props.specialTopBox
        ? props.specialTopBox(loading, data, dataOrig)
        : null}
      <Antd.Table
        className="table-striped-rows table-small"
        size="small"
        showSorterTooltip={false}
        loading={loading}
        dataSource={data}
        pagination={pagination}
        rowKey={props.rowKey ? props.rowKey : null}
        rowSelection={props.rowSelection ? props.rowSelection : null}
        expandable={props.expandable ? props.expandable : null}
        scroll={{ y: 540 }}
        summary={props.summary ? (pageData) => {
          return props.summary(loading, data, dataOrig, pageData);
        } : null}
        footer={props.footer ? (currentPageData) => {
          return props.footer(loading, data, dataOrig, currentPageData);
        } : null}
        onChange={async (pagination, filters, sorter) => {
          if (props.useURLPagination) {
            queryParams.set(`${props.name ? props.name + '_' : ''}page`, pagination.current);
            window.history.pushState('', '', '?' + queryParams.toString());
          }

          setPagination(pagination);

          if (sorter.order === "descend") {
            let newSorter = { field: sorter.field, order: sorter.order };
            await datasetgridSortsStorage.setItem(STORAGE_KEY, newSorter);
            setSorter(newSorter);
            setData(Lodash.orderBy(data, [sorter.field], ['desc']));
          } else {
            let newSorter = { field: sorter.field, order: sorter.order };
            await datasetgridSortsStorage.setItem(STORAGE_KEY, newSorter);
            setSorter(newSorter);
            setData(Lodash.orderBy(data, [sorter.field], ['asc']));
          }
        }}
      >
        {Lodash.sortBy(columns, ['settings.datagrid.order']).map(column => {
          return column.settings.datagrid.visible
            ? <Antd.Table.Column
              key={column.fieldName}
              dataIndex={column.fieldName}
              title={column.name}
              fixed={column.settings.datagrid.fixed ? column.settings.datagrid.fixed : null}
              align={column.settings.datagrid.alignment ? column.settings.datagrid.alignment : 'left'}
              width={column.settings.datagrid.width ? column.settings.datagrid.width : null}
              sorter={column.settings.datagrid.sorter ? column.settings.datagrid.sorter : false}
              sortOrder={sorter && sorter.field && sorter.order && sorter.field == column.fieldName ? sorter.order : null}
              render={(value, record) => {
                let newText = value;

                if (column.settings.datagrid.link !== undefined
                  && column.settings.datagrid.link) {
                  newText = <Router.Link to={evalTemplate(column.settings.datagrid.link, { record })}>{newText}</Router.Link>;
                }

                if (column.settings.datagrid.render !== undefined
                  && column.settings.datagrid.render
                  && column.settings.datagrid.render !== "") {
                  let decodedRender = atob(column.settings.datagrid.render);
                  const history = Router.useHistory();
                  const params = Router.useParams();
                  newText = (
                    <MDXProvider>
                      <MDX
                        scope={{
                          Antd,
                          AntdIcons,
                          COM,
                          Moment,
                          Lodash,
                          Router,
                          params: params,
                          push: history.push,
                          userProfile: auth.userProfile,
                          reload: init,
                          record,
                          value,
                        }}
                      >
                        {decodedRender}
                      </MDX>
                    </MDXProvider>
                  );
                }

                return newText;
              }}
            />
            : null
        })}
      </Antd.Table>
      <Antd.Drawer
        title={'Filters'}
        width={700}
        open={drawerShowed}
        closable={true}
        onClose={() => setDrawerShowed(false)}
      >
        <COM.DatasetFilters
          connector={props.connector}
          dataset={props.dataset}
          initialValues={filters === null ? props.filters : filters}
          initialFilters={props.filters}
          onChange={async (values) => {
            if (props.onFiltersChange) {
              props.onFiltersChange(values);
            }
            let filtersItem = {
              filters: JSON.stringify(values),
              loaded: false,
            }
            await datasetgridFiltersStorage.setItem(STORAGE_KEY, filtersItem);
            setFilters(values);
            setLoading(true);
            await loadData(values);
            setLoading(false);
            filtersItem.loaded = true;
            await datasetgridFiltersStorage.setItem(STORAGE_KEY, filtersItem);
          }}
        />
      </Antd.Drawer>
    </div>
  );
  // });
};